import { mapState, mapActions, mapGetters } from 'vuex';
import {
  formatPrice,
  convertGQLToProductJson,
  htmlDecode,
} from 'utils/general';
import debounce from 'lodash/debounce';
import { i18n } from 'lib/i18n.js';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import {
  addToCartEventData,
  trackRemoveFromCart,
  trackEvent,
  buildItemTrackingData,
  setLocalStorageProperties,
  trackAddToCart,
  trackProductClick,
} from 'lib/tracking';
import datalayer from 'lib/datalayer';
import productBundle from 'mixins/product-bundle';

export default {
  i18n,
  components: {
    'quantity-selector': () =>
      import('../quantity-selector/quantity-selector.vue'),
    'vue-image': () => import('../vue-image/vue-image.vue'),
  },
  mixins: [productBundle],
  name: 'cart-item',
  mixins: [productBundle],
  props: {
    single: {
      type: Object,
    },
    cartPage: {
      type: [Object, Number],
    },
    isChild: {
      type: Boolean,
      default: false,
    },
    from: {
      type: String,
      default: '',
    },
    activeSamples: {
      type: Array,
    },
    index: {
      type: Number,
    },
  },
  data() {
    return {
      model: false,
      showQTY: true,
      insoleVariant: false,
      isUpdating: false,
      showChildren: false,
      upsellBundle: null,
      showUpsell: true,
      showPromo: true,
      dataReady: false,
      isVirtualBundle: false,
      qtyErrorMessage: '',
    };
  },
  watch: {
    single(_new, _old) {
      if (_old.quantity !== _new.quantity) {
        setTimeout(() => {
          this.showQTY = true;
        }, 100);
      }

      if (_new.quantity > this.maxQuantity) {
        setTimeout(() => {
          this.updateQuantity(this.maxQuantity);
        }, 2000);
      }

      this.isVirtualBundle =
        _new.properties?._bundle_type == 'virtual bundle' &&
        _new?.item_components?.length > 0;
    },
  },
  async mounted() {
    if (
      !this.single.properties._bundle &&
      this.single.quantity > this.maxQuantity
    ) {
      this.updateQuantity(this.maxQuantity);
    }

    await this.getUpsellBundle();
    this.dataReady = true;
    this.isBundle = this.single.isBundle;
    this.isVirtualBundle =
      this.single.properties?._bundle_type == 'virtual bundle' &&
      this.single?.item_components?.length > 0;
  },
  computed: {
    ...mapState('global', ['cart', 'isMobile']),
    ...mapState('cart', ['cart_insoles', 'loading', 'productsData']),
    ...mapGetters('global', [
      'gwpCollectionData',
      'GWPApplicableProducts',
      'GWPRewardProducts',
    ]),
    cartItems() {
      return this.cart.items || [];
    },
    option() {
      return `${this.single.options_with_values[0].name}: ${this.single.options_with_values[0].value}`;
    },
    maximumText() {
      return this.$t('cart.label.maximum', {
        count: this.maxQuantity,
      });
    },
    removeText() {
      return this.$t('cart.gwp.remove');
    },
    editText() {
      return this.$t('cart.gwp.edit');
    },
    maxQuantity() {
      const maxQuantityProp =
        this.single.properties?.[`_${Shopify.country.toUpperCase()}`];
      const sameIdProducts = this.cart.items.filter(
        (item) => item.product_id === this.single.product_id,
      );
      const sameIDProductsQuantity = sameIdProducts.reduce(
        (acc, item) => acc + item.quantity,
        0,
      );
      let quantity = maxQuantityProp || 5;

      if (sameIdProducts.length > 1 && sameIDProductsQuantity >= quantity) {
        quantity = this.single.quantity;
      }
      return this.single.properties?.[maxQuantityProp] || quantity;
    },
    isGWP() {
      return this.single.properties?._gwp;
    },
    isGiftCard() {
      return this.single.product_type === 'Gift Card';
    },
    hasUpsellBundle() {
      return this.single.properties?._upsell_bundle;
    },
    upsellBundlePrice() {
      if (!this.upsellBundle) return '';
      return formatPrice(this.upsellBundle.price);
    },
    upsellText() {
      if (!this.upsellBundle || this.bundleFullPrice <= this.upsellBundle.price)
        return '';

      const saving_price = formatPrice(
        this.bundleFullPrice - this.upsellBundle.price,
      );
      return this.$t('cart.upsell.savings', {
        price: saving_price,
      });
    },
    isUpsellDismissed() {
      if (!this.upsellBundle || !window.sessionStorage.getItem('bundleUpsell'))
        return false;

      const dismissedUpsellArray =
        window.sessionStorage.getItem('bundleUpsell');

      return dismissedUpsellArray.includes(this.upsellBundle.id);
    },
    isGwpCollectionUpsellDismissed() {
      if (window.sessionStorage.getItem('gwpCollectionUpsell')) return true;
      return false;
    },
    showGwpCollectionUpsell() {
      let res = true;
      if (
        this.GWPApplicableProducts >= +this.gwpCollectionData.min_quantity ||
        !this.GWPRewardProducts?.length
      ) {
        res = false;
      }
      return res;
    },
    isPromoDismissed() {
      if (!window.sessionStorage.getItem('productPromo')) return false;
      const dismissedPromoArray = window.sessionStorage.getItem('productPromo');
      return dismissedPromoArray.includes(this.single.id);
    },
    isPromoApplied() {
      return !!this.single.discounts.length;
    },
    isCouponProduct() {
      return this.single.properties?._coupon;
    },
    computedQuantity() {
      return this.single.quantity;
    },
    coupon() {
      return this.single.properties?._coupon || false;
    },
    threshold() {
      return this.single.properties?._couponThreshold || 0;
    },
    thresholdMessage() {
      return this.single.properties?._thresholdMessage || '';
    },
    productTitle() {
      return this.single.product_title.split(
        window.__GLOBAL__.productDelimiter,
      )[0];
    },
    productUrl() {
      if (
        this.isChild &&
        this.single?.isChild &&
        this.single.properties?._bundle_product_url
      ) {
        return this.single.properties._bundle_product_url;
      }
      return this.isBundleBuilderItem || this.isVirtualBundle
        ? this.single.url || ''
        : this.single.properties?._bundle_url
        ? this.single.properties?._bundle_url
        : this.single.properties?._url || this.single.url;
    },
    cartUpsellUrl() {
      if (!this.hasUpsellBundle) return '';
      return `${window.Shopify.routes.root}products/${this.single.properties?._upsell_bundle}`;
    },
    hasDiscount() {
      return this.hasSubscription
        ? parseInt(this.single.compare_at_price) >
            parseInt(this.single.discounted_price)
        : parseInt(this.single.compare_at_price) > parseInt(this.single.price);
    },
    hasPromotion() {
      return parseInt(this.single.line_level_total_discount) > 0;
    },
    promotionText() {
      return this.single.line_level_discount_allocations[0].discount_application
        .title;
    },
    productPromo() {
      if (
        !this.single.properties._promo_text ||
        this.single.properties._promo_text === 'null' ||
        !this.single.properties._promo_link ||
        this.single.properties._promo_link === 'null'
      )
        return false;
      let link = this.single.properties._promo_link;
      const root = window.Shopify.routes.root;

      if (root !== '/' && !!this.single.properties._promo_link) {
        const [domain, promo_link] =
          this.single.properties._promo_link.split('/products/');
        link = domain + root + 'products/' + promo_link;
      }

      return {
        promoText:
          this.single.properties?._promo_text &&
          !this.single.properties._promo_text.includes('%')
            ? decodeURIComponent(this.single.properties?._promo_text)
            : '',
        promoLink: link,
      };
    },
    isFree() {
      return !!this.single.line_price;
    },
    isSimple() {
      return this.$store.getters['cart/cartItemProductIsSimple'](this.single);
    },
    dataId() {
      if (this.isBundle) {
        return `_${this.single.id}`;
      }

      return `${this.from}_${this.single.id}`;
    },
    isThresholdReached() {
      return (
        this.threshold <=
        this.cartItems.reduce(
          (prev, curr) =>
            prev + curr.properties?._coupon ? 0 : curr.final_line_price,
          0,
        )
      );
    },
    hasSubscription() {
      return this.single.selling_plan_allocation?.selling_plan?.name || false;
    },
    preorder() {
      return {
        enabled: !!this.single.properties?._Preorder,
        text: this.single.properties?._Preorder || '',
      };
    },
    imageWidth() {
      return `${this.single.featured_image.width}`;
    },
    imageHeight() {
      return `${this.single.featured_image.height}`;
    },
    message() {
      let hasMessage = false;
      let messageText = '';
      const {
        _bundle: isBundle,
        _bundle_validated: validated,
        _bundle_url: url,
        _restricted: restrictionShippingCode = false,
      } = this.single.properties;

      if (isBundle && !(validated || validated === undefined)) {
        const bundleAnchor = url
          ? `<a href="${window.Shopify.routes.root}${url.slice(1)}">bundle</a>`
          : 'bundle';
        hasMessage = true;
        messageText = `There was an error with your ${bundleAnchor} discount, likely due to having switched countries. Please remove it and try again.`;
      }

      if (
        restrictionShippingCode &&
        !restrictionShippingCode.split('|').includes(window.shippingCode)
      ) {
        hasMessage = true;
        messageText = `This item cannot be shipped to your selected country. Please remove it and try again.`;
      }

      if (!hasMessage) return '';
      return `
        <span class="p2">
          ${messageText}
        </span>
      `;
    },
    colorId() {
      const productTags = this.single.properties?._product_tags
        ?.split(',')
        .map((tag) => {
          if (tag.includes('%')) {
            return '';
          }
          return decodeURIComponent(tag);
        })
        .filter((tag) => tag !== '');
      const colorTag = productTags?.find((tag) => tag.includes('Color ID'));

      if (colorTag) {
        return colorTag.split('|')[1].toLowerCase();
      }
      return '';
    },
    getShadeTitle() {
      if (this.single.variant_title) {
        return this.single.variant_title;
      } else if (this.single.product_title) {
        const singleTitleParts = (this.single.product_title).split('—');
        if (singleTitleParts.length > 1) {
          return singleTitleParts[1].trim();
        }
      }
      return null;
    },
    bundleText() {
      return this.$t('cart.general.bundles', {
        count: this.single?.items?.length
          ? this.single.items.length
          : this.single?.item_components?.length || 0,
      });
    },
    bundleValue() {
      if (!this.isVirtualBundle || !this.single.properties?._bundle_full_price)
        return '';

      return this.$t('products.product.bundle_price', {
        price: formatPrice(this.single.properties._bundle_full_price, true),
      });
    },
    gwpCollectionImage() {
      if (!this.gwpCollectionData.image) return false;
      return this.gwpCollectionData.image;
    },
    gwpCollectionCopy() {
      if (!this.gwpCollectionData.copy) return '';
      return htmlDecode(this.gwpCollectionData.copy);
    },
    gwpCollectionButtonCopy() {
      if (!this.gwpCollectionData.button_text) return '';
      return htmlDecode(this.gwpCollectionData.button_text);
    },
    isBundleBuilderItem() {
      return (
        this.single?.product_type === 'Bundle Builder' ||
        this.single.properties?._bundle_builder == 'true' ||
        false
      );
    },
    cartDiscountText() {
      if (!this.isBundleBuilderItem || !this.single.properties?._bundle_byob)
        return false;
      const bundleData = JSON.parse(this.single.properties._bundle_byob);
      return this.$t('bundle_builder.cart_discount_msg').replace(
        '{percentage}',
        bundleData?.discount_percentage || '0',
      );
    },
    bundleItemTotalPrice() {
      if (!this.single.properties?._bundle_byob) {
        return formatPrice(0, true, true);
      }

      const totalPrice = this.single.item_components.reduce((acc, item) => {
        let originalPrice;
        if (
          this.single?.product_type === 'Bundle Builder' ||
          this.single.properties?._bundle_builder
        ) {
          originalPrice = item?.original_price
            ? Number(item.original_price)
            : 0;
        } else {
          originalPrice = item?.final_price ? Number(item.final_price) : 0;
        }

        return acc + originalPrice;
      }, 0);

      if (this.single?.properties?._bundle_discount_value) {
        const discount = parseInt(
          this.single.properties._bundle_discount_value,
          10,
        );

        if (!isNaN(discount) && discount > 0 && discount < 100) {
          const discountedPrice = totalPrice * (1 - discount / 100);
          return formatPrice(discountedPrice, true, true);
        }
      }

      return formatPrice(this.single.original_price, true, true);
    },
    bundleItemComparePrice() {
      if (
        !this.single?.item_components ||
        this.single.item_components.length === 0 ||
        !this.single.properties?._bundle_byob
      ) {
        return formatPrice(0, true, true);
      }

      const totalCompareAtPrice = this.single.item_components.reduce(
        (acc, item) => {
          const originalPrice = item?.original_price
            ? Number(item.original_price)
            : 0;
          return acc + originalPrice;
        },
        0,
      );

      const bundleData = JSON.parse(this.single.properties._bundle_byob);

      if (bundleData?.discount_percentage) {
        const discount = parseInt(bundleData?.discount_percentage, 10);

        if (!isNaN(discount) && discount > 0 && discount < 100) {
          const totalPriceWithoutDiscount = Math.ceil(
            totalCompareAtPrice / (1 - discount / 100),
          );
          return formatPrice(Math.ceil(totalPriceWithoutDiscount), true, true);
        }
      }

      return formatPrice(totalCompareAtPrice, true, true);
    },
  },
  methods: {
    ...mapActions('cart', ['updateBundleCartItems', 'updateCartLine']),
    ...mapActions('global', ['openModal', 'removeAltProductToList']),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
      'updateItemToUpdate',
      'updateAction',
    ]),
    onClickItem() {
      const otherData = {
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
        index: this.index + 1,
      };

      const listData = {
        listName: 'Mini-cart',
        listId: 'mini-cart',
        index: this.index + 1,
        moduleName: 'Cart Item',
      };

      setLocalStorageProperties(otherData, listData);
      if (this.isBundleBuilderItem) {
        trackEvent({
          event: 'select_bundle',
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder PLP Product Card',
          item_list_id: listData.listId,
          item_list_name: listData.listName,
          bundle_type: 'bundle builder',
          bundle_name: htmlDecode(this.single.properties?._origin_product),
          bundle_id: String(this.single.properties?._bundle),
          price: parseInt(this.single.original_price, 10) / 100,
          currency: window.Shopify.currency.active,
          index: this.index + 1,
        });
        window.location = this.productUrl;
      } else {
        let itemObject = [];

        if (this.isVirtualBundle) {
          otherData.bundleName = this.single.title;
          otherData.bundleId = this.single.id;
          listData.bundleName = this.single.title;
          listData.bundleId = this.single.id;
          itemObject = this.single.item_components.map((item) => {
            item.isBundle = true;
            const data = buildItemTrackingData(item, 1, otherData);
            const payload = {
              ...data,
              item_category: item.product_type,
              avg_stars: item.properties?._product_rating || undefined,
            };
            return payload;
          });
          window.location = this.productUrl;
        } else {
          itemObject = buildItemTrackingData(
            { ...this.single, available: true },
            1,
            otherData,
          );
          itemObject = {
            ...itemObject,
            item_category: this.single.product_type,
            avg_stars: this.single.properties?._product_rating || undefined,
          };
          window.location = `${Shopify.routes.root}products/${this.single.handle}`;
        }
        trackProductClick('select_item', itemObject, listData);
      }
    },
    updateQuantity: debounce(function (quantity) {
      this.isUpdating = true;
      this.qtyErrorMessage = '';
      let currentQuantity = this.single.quantity;

      this.updateCartLine({
        quantity,
        id: this.single.key,
        callback: () => {
          this.isUpdating = false;
        },
        errorCallback: (error) => {
          this.isUpdating = false;
          this.qtyErrorMessage = error?.message || error;
        },
      });

      const extraData = {
        addToCartType: 'Cart Panel',
        sku:
          typeof this.single.sku !== 'undefined'
            ? this.single.sku
            : this.single.id,
      };

      let action = 'addToCart';
      if (currentQuantity > quantity) {
        action = 'removeFromCart';

        trackRemoveFromCart({
          product: this.single,
        });
      } else {
        trackAddToCart({
          product: this.single,
          isCartItem: true,
          otherData: {
            siteSection: window.__GLOBAL__.siteSection,
            componentName: 'Mini cart',
            moduleName: 'cart item',
            ...(this.single.isBundle &&
              this.single.properties?._bundle_builder == 'true' && {
                bundleType: 'bundle builder',
              }),
          },
        });
      }
      datalayer.dataLayerPush(
        action,
        addToCartEventData(this.single, 1, extraData),
      );
    }, 450),
    async onRemove() {
      this.updateCartLine({
        quantity: 0,
        id: this.single.key,
      });
      this.removeAltProductToList(this.single.id || this.single.key);

      // tracks
      const trackingProduct = this.productsData[this.single.handle];

      if (trackingProduct.tags?.indexOf('donation') > -1) {
        const donationValue = formatPrice(this.single.price);

        trackEvent({
          event: 'donation_removed',
          donation_value: donationValue,
        });
        return;
      }

      trackRemoveFromCart({
        product: this.single,
        quantity: this.single.quantity,
      });
    },
    removeBundle() {
      this.updateBundleCartItems({
        quantity: 0,
        items: this.single.items,
      });
    },
    async edit(lineItem) {
      const storefrontResponse = await storefront.request(gqlQueries.product, {
        handle: lineItem.handle,
      });
      const product = convertGQLToProductJson(storefrontResponse.product);

      if (lineItem.properties._gwp) {
        product.isGWP = true;
        product.activeVariant = product.variants.find(
          (variant) => +variant.id === +this.single.id,
        );
      }
      this.updateAction('edit');
      this.updateItemToUpdate(lineItem.id);
      this.updateModalTitle(this.$t('quick_view.edit_item_title'));

      const ctaText = this.isMobile
        ? this.$t('quick_view.update')
        : this.$t('quick_view.update_my_bag');
      this.updateCtaText(ctaText);
      this.setProduct(product);
      this.openModal('quick-view');
    },
    priceFormat(price, freeIfZero = true) {
      return price > 0 || !freeIfZero
        ? formatPrice(price, true, true)
        : this.$t('cart.general.free');
    },
    toggleBundleChildren() {
      this.showChildren = !this.showChildren;
    },
    async getUpsellBundle() {
      if (!this.single.properties?._upsell_bundle) return;

      const storefrontResponse = await storefront.request(gqlQueries.product, {
        handle: this.single.properties?._upsell_bundle,
      });

      this.upsellBundle = convertGQLToProductJson(storefrontResponse.product);

      if (this.upsellBundle.isBundle) {
        await this.fetchBundleProductsByHandles(
          this.upsellBundle.metafields.accentuate.bundled_products,
        );
      }
    },
    hideUpsell() {
      this.showUpsell = false;

      const dismissedItems = JSON.parse(
        sessionStorage.getItem('bundleUpsell') || '[]',
      );

      if (!dismissedItems.includes(this.upsellBundle.id)) {
        dismissedItems.push(this.upsellBundle.id);
        sessionStorage.setItem('bundleUpsell', JSON.stringify(dismissedItems));
      }
    },
    hideCollectionPromo() {
      this.showGwpCollectionUpsell = false;

      if (!sessionStorage.getItem('gwpCollectionUpsell')) {
        sessionStorage.setItem('gwpCollectionUpsell', true);
      }
    },
    openGWPCollection() {
      this.openModal('gwp-collection');
    },
    hidePromo() {
      this.showPromo = false;

      const dismissedItems = JSON.parse(
        sessionStorage.getItem('productPromo') || '[]',
      );

      if (!dismissedItems.includes(this.single.id)) {
        dismissedItems.push(this.single.id);
        sessionStorage.setItem('productPromo', JSON.stringify(dismissedItems));
      }
    },
  },
  template: /* html */ `
      <div
        class="cart-item"
        :class="{'is-bundle':isBundle, 'is-child':isChild}"
        :data-id="!isChild ? dataId : ''"
      >
        <div class="w1 relative f jcb">
          <a
            :href="productUrl" class="header-cart__element-image relative" v-if="!isChild"
            @click.prevent="onClickItem"
          >
            <div
              is="vue-image"
              :src="single.featured_image.url"
              :size="200"
              :alt="single.product_title"
              spinner-size="sm"
              css="fit-cover"
              :width="imageWidth"
              :height="imageHeight">
            </div>
          </a>

          <div class="header-cart__element-image relative" v-else>
            <div
              is="vue-image"
              :src="single.featured_image.url"
              :size="200"
              :alt="single.product_title"
              spinner-size="sm"
              css="fit-cover"
              :width="imageWidth"
              :height="imageHeight">
            </div>
          </div>

          <div class="header-cart__element-data">
            <div class="relative fg header-cart__element-contents">
              <span class="heading6" v-if="isBundleBuilderItem && !isChild">
                {{ $t('bundle_builder.cart_messaging') }}
              </span>
              <a :href="productUrl"
                class="header-cart__element-title heading6"
                v-text="productTitle" v-if="!isChild"
                @click.prevent="onClickItem">
              </a>
              <h3 :href="productUrl"
                class="header-cart__element-title p bold"
                :class="{ 'p bold': isChild , 'heading6' : !isChild }"
                v-text="productTitle" v-else>
              </h3>
              <div class="header-cart__element-box">
                <p v-if="coupon" class="header-cart__element-title__coupon p2 mb025">
                  Use the coupon <span class="bold" v-text="coupon"></span> at checkout to reflect the discount
                </p>
                <p v-if="!isThresholdReached" class="header-cart__element-title__coupon p2 mb025">
                  You need to spend at least {{ thresholdMessage }} to use this code
                </p>
                <p v-if="hasSubscription" v-text="hasSubscription" class="p2 mb025" />
                <p class="header-cart__element-title_Preorder p2 mb025" v-if="preorder.enabled">
                  {{ $t('products.preorder.cart_label') }}
                </p>
                <div class="header-cart__element-option f aic" v-if="!isBundle && single.variant_title || !isBundle && getShadeTitle">
                  <span class="header-cart__element-option__value f aic" v-if="single.variant_title || getShadeTitle">
                    <span class="header-cart__element-option__shade" :class="['fenty-color-' + colorId]" v-if="colorId"></span>
                    <span class="header-cart__variant__title" v-if="single.variant_title" v-text="single.options_with_values[0].value"></span>
                    <span class="header-cart__variant__title_alternate" v-if="getShadeTitle && !single.variant_title" v-text="getShadeTitle"></span>
                    <span class="header-cart__option_title" v-if="single.options_with_values[1]" v-text="'/  '+single.options_with_values[1].value"></span>
                  </span>
                  <a v-if="!isChild && !isSimple && !isCouponProduct && !hasSubscription && !isGiftCard" href="javascript:void(0)" class="header-cart__element-edit" @click="edit(single)" v-text="editText"></a>
                </div>
                <div v-if="message.length" v-html="message" class="header-cart__element-message">
                </div>
                <div
                  v-if="!isChild"
                  class="cart-item__price OneLinkNoTx p1"
                >
                  <section :class="{'cart-item__price--flex': single?.properties._bundle_compare_price || single?.properties._bundle_byob}">
                    <template v-if="single.compare_at_price > 0 && !isBundle && !isGWP">
                      <div
                        v-if="hasDiscount"
                        class="inline bold discounted cart-item__compare_at_price"
                        :class="{'stroked-price': single?.properties._gwp}"
                        v-text="priceFormat(single.compare_at_price, false)"
                      />
                      <div class="inline" :class="{'bold red': hasDiscount}">
                        <span v-if="hasSubscription" v-text="priceFormat(single.discounted_price)"></span>
                        <span v-else v-text="priceFormat(single.price)"></span>
                      </div>
                    </template>

                    <template v-else-if="isBundle && single?.properties._bundle_byob">
                      <div
                        class="inline bold discounted cart-item__compare_at_price"
                        v-text="bundleItemComparePrice"
                      />
                      <div class="inline bold red">
                        <span  v-text="bundleItemTotalPrice"></span>
                      </div>
                    </template>

                    <template v-else-if="hasPromotion && !isBundle">
                      <div
                        class="inline bold discounted cart-item__compare_at_price"
                        v-text="priceFormat(single.price, false)"
                      />
                      <div class="inline bold red">
                        <span v-text="priceFormat(single.discounted_price)"></span>
                      </div>
                    </template>


                    <template v-else>
                      <div class="inline">
                        <span v-if="isBundle" v-text="priceFormat(single.properties._bundle_price)"></span>
                        <span v-else v-text="priceFormat(single.price)"></span>
                      </div>
                    </template>
                    <span v-if="isVirtualBundle" class="grey">{{bundleValue}}</span>
                  </section>

                  <div v-if="cartDiscountText" class="red cart-item__discount_text">{{ cartDiscountText }}</div>
                  <div v-if="qtyErrorMessage" v-html="qtyErrorMessage" class="red cart-item__err_text"></div>

                  <p class="header-cart__price-promotion red" v-if="hasPromotion && !isBundle" v-text="promotionText"></p>
                </div>
              </div>
              <div class="relative header-cart__qty f jcb aic" v-if="!isChild" :class="{'header-cart__qty--loading': (loading === single.id)}">
                <div
                  is="quantity-selector"
                  v-if="!isGWP && !isCouponProduct"
                  @change="updateQuantity"
                  :value="computedQuantity"
                  :min="0"
                  :max="maxQuantity"
                  :small="true"
                  ></div>
                <p v-if="computedQuantity > maxQuantity" v-text="maximumText" class="header-cart__element-maximun p2 red"></p>
                <button type="button" class="p2 header-cart__remove" @click="onRemove" v-text="removeText"></button>
                <p v-if="computedQuantity > maxQuantity" v-text="maximumText" class="header-cart__element-maximun-mo p2 red"></p>
                <div v-if="isUpdating" class="quantity-selector--loading"></div>
              </div>
            </div>
          </div>
        </div>


        <div v-if="single.item_components?.length > 0" class="cart-item__bundle-items">
          <button @click="toggleBundleChildren" class="p cart-item__bundle-button" :class="{'is-active': showChildren}">
            {{bundleText}}
            <span class="cart-item__bundle-icon"></span>
          </button>
          <div class="cart-item__bundle-items-wrapper" v-show="showChildren">
            <cart-item v-for="item in single.item_components"
              :key="single.id+'_'+item.id+'_'+item.properties._bundle_index"
              :single="item"
              :cartPage="cartPage"
              :isChild="true"
            />
          </div>
        </div>

        <div v-if="single.hasCollectionPromo && showGwpCollectionUpsell && !isGwpCollectionUpsellDismissed()"
          class="cart-upsell relative">
            <span class="cart-upsell__icon pointer" @click.prevent="hideCollectionPromo"></span>
            <div class="cart-upsell__image" v-if="gwpCollectionImage">
              <div
                is="vue-image"
                :src="gwpCollectionImage"
                :size="100"
                :alt="gwpCollectionCopy"
                spinner-size="sm"
                css="fit-cover"
                :width="'50'"
                :height="'50'">
              </div>
            </div>
            <div class="cart-upsell__content cart-upsell__content--collection">
              <h3 class="cart-upsell__title p bold" v-text="gwpCollectionCopy"></h3>
              <span class="p underline pointer" v-text="gwpCollectionButtonCopy" @click="openGWPCollection"></span>
            </div>
        </div>

        <div v-if="dataReady && upsellBundle && !isUpsellDismissed && showUpsell && !productPromo">
          <a :href="cartUpsellUrl" class="cart-upsell relative">
            <span class="cart-upsell__icon" @click.prevent="hideUpsell"></span>
            <div class="cart-upsell__image" v-if="upsellBundle.featuredImage">
              <div
                is="vue-image"
                :src="upsellBundle.featuredImage"
                :size="100"
                :alt="upsellBundle.title"
                spinner-size="sm"
                css="fit-cover"
                :width="'50'"
                :height="'50'">
              </div>
            </div>
            <div class="cart-upsell__content">
              <h3 class="cart-upsell__title p bold" v-text="upsellBundle.title"></h3>
              <span class="p bold" v-text="upsellBundlePrice"></span>
              <span class="p bold" v-html="bundleFullPriceText" v-if="bundleFullPrice !== this.upsellBundle.price"></span>
              <p class="p underline" v-text="upsellText"></p>
            </div>
          </a>
        </div>

        <a :href="productPromo.promoLink" v-if="productPromo && showPromo && !isPromoDismissed && !isPromoApplied" class="cart-upsell relative">
          <span class="cart-upsell__icon" @click.prevent="hidePromo"></span>
          <span class="p" v-text="productPromo.promoText"></span>
        </a>
      </div>
      `,
};
